import { toast } from 'react-toastify';
import {
  CLICK_ACTIONS,
  REIMBURSEMENT_CLIAM_TYPES,
  ReimbursementClaimType,
} from '../../utils/Constant';
import {
  AccomodationFromData,
  ApproveReimbursementFilterType,
  ClaimType,
  ColumnHeaderType,
  FoodFromData,
  MiscellaneousFromData,
  TravelFromData,
} from '../../utils/Types';
import {
  blankValueGetter,
  convertDateToISODate,
  dateValueGetterWithTime,
  formatNumberWithComma,
  handleApproveReimbursementActionColors,
  isAPIFailed,
  isAPISuccess,
} from '../../utils/Util';
import moment from 'moment';
import { ThunkDispatch } from 'redux-thunk';
import {
  approveOrRejectReimbursement,
  fetchReimbursementLocationDetails,
  postApplyReimbursementAttachments,
} from '../../features/reimbursementSlice';

export const ApplyReimbursementColumnHeader: ColumnHeaderType[] = [
  {
    name: 'Task Id',
    field: 'taskId',
    sortable: false,
    clickable: false,
    action: CLICK_ACTIONS.VIEW_TASK,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Task Name',
    field: 'taskName',
    sortable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Source',
    field: 'sourceName',
    sortable: true,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Action',
    field: 'action',
    clickable: true,
    action: CLICK_ACTIONS.APPLY_REIMBURSEMENT,
    value: 'Raise a claim',
  },
];
export const ApproveReimbursementColumnHeader: ColumnHeaderType[] = [
  {
    name: 'Claim Id',
    field: 'claimId',
    showProfilePhoto: true,
    clickable: false,
    valueGetter: (data: any, field?: string) => {
      const value = data[field as keyof any];
      return value?.toString()?.padStart(4, '0') || '-';
    },
  },
  {
    name: 'Employee Id',
    field: 'employeeUniqueId',
    sortable: false,
    clickable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Employee Name',
    field: 'fullName',
    sortable: false,
  },
  {
    name: 'Zone',
    field: 'zone',
    sortable: false,
    valueGetter: blankValueGetter,
    valueFromLocalStorage: true,
  },
  {
    name: 'State',
    field: 'state',
    clickable: false,
    valueGetter: blankValueGetter,
    valueFromLocalStorage: true,
  },
  {
    name: 'District',
    field: 'district',
    clickable: false,
    valueGetter: blankValueGetter,
    valueFromLocalStorage: true,
  },

  {
    name: 'Claim Date & Time',
    field: 'createdOn',
    clickable: false,
    valueGetter: dateValueGetterWithTime,
  },
  {
    name: 'Type of Claim',
    field: 'claimType',
    clickable: false,
    valueGetter: blankValueGetter,
    toolTipAfter: 25,
  },
  {
    name: 'Claim Amount',
    field: 'totalClaimAmount',
    clickable: false,
    valueGetter: (data: any, field?: string) => {
      const value = data[field as keyof any];
      return formatNumberWithComma(value);
    },
  },
  {
    name: 'Status',
    field: 'statusName',
    clickable: true,
    action: CLICK_ACTIONS.APPROVE_REIMBURSEMENT,
    // actionGetter: approveReimbursementActionGetter,
    // valueGetter: blankValueGetter,
    classNameGetter: handleApproveReimbursementActionColors,
  },
];

/**
 * Validates whether the fields contain values.
 */
const validateFields = (fields: any[]) => {
  return fields.every((field: any) => {
    if (typeof field === 'string') {
      return field.trim();
    }
    return Number(field);
  });
};

/**
 * Checks the length of the images array.
 */
const validateImages = (imagesList: any, type: ReimbursementClaimType) => {
  return imagesList?.[REIMBURSEMENT_CLIAM_TYPES[type]]?.length;
};

/**
 * Displays a toast message to provide feedback to the user
 */
const showError = (message: string) => {
  toast.error(message);
  return false;
};

/**
 * Validates the reimbursment section fields and displays a toast message if errors are found.
 */
export const validateSection = (
  imagesList: any,
  fields: any,
  imagesType: any,
  errorMessageFields: string,
  errorMessageImages: string
) => {
  if (!validateFields(fields)) {
    return showError(errorMessageFields);
  }
  if (!validateImages(imagesList, imagesType)) {
    return showError(errorMessageImages);
  }
  return true;
};

/**
 * Validates all fields in the section.
 */
export const validation = (
  imagesList: any,
  claimType: ClaimType,
  travelFormData: TravelFromData,
  foodFormData: FoodFromData,
  accommodation: AccomodationFromData,
  miscellaneous: MiscellaneousFromData
) => {
  if (claimType?.Travel) {
    const { locationA, locationB, totalDistance, totalExpenses, reason } =
      travelFormData || {};
    if (
      !validateSection(
        imagesList,
        [locationA, locationB, totalDistance, totalExpenses, reason],
        'Travel',
        'Please fill in all mandatory fields in the Travel section.',
        'Please upload the required images for the Travel section.'
      )
    )
      return false;
  }

  if (claimType?.Food) {
    const { locationA, totalExpenses } = foodFormData || {};
    if (
      !validateSection(
        imagesList,
        [locationA, totalExpenses],
        'Food',
        'Please fill in all the mandatory fields in the Food section.',
        'Please upload the required images for the Food section.'
      )
    )
      return false;
  }

  if (claimType?.Accommodation) {
    if (
      moment(accommodation?.claimFromDate, 'DD-MM-YYYY hh:mm a')?.isAfter(
        moment(accommodation?.claimToDate, 'DD-MM-YYYY hh:mm a')
      )
    ) {
      return showError(
        'In Accommodation, Claim From Date cannot be greater than Claim To Date'
      );
    }
    const {
      claimFromDate,
      claimToDate,
      totalExpenses,
      reason,
      locationA,
      zone,
      state,
      district,
      city,
      hotelName,
    } = accommodation || {};
    if (
      !validateSection(
        imagesList,
        [
          claimFromDate,
          claimToDate,
          totalExpenses,
          reason,
          locationA,
          zone,
          state,
          district,
          city,
          hotelName,
        ],
        'Accommodation',
        'Please fill in all the mandatory fields in the Accommodation section',
        'Please upload the required images for the Accommodation section.'
      )
    )
      return false;
  }

  if (claimType?.Miscellaneous) {
    const { claimFromDate, locationA, totalExpenses } = miscellaneous || {};
    if (
      !validateSection(
        imagesList,
        [claimFromDate, locationA, totalExpenses],
        'Miscellaneous',
        'Please fill in all the mandatory fields in the Miscellaneous section',
        'Please upload the required images for the Miscellaneous section.'
      )
    )
      return false;
  }
  return true;
};

/**
 * Creates a payload for all sections.
 */
export const getReqData = (
  imagesList: any,
  claimType: ClaimType,
  travelFormData: TravelFromData,
  foodFormData: FoodFromData,
  accommodation: AccomodationFromData,
  miscellaneous: MiscellaneousFromData,
  selectedTask: any
) => {
  const claimDetails = [];
  if (claimType?.Travel) {
    claimDetails.push({
      ...travelFormData,
      claimFromDate: convertDateToISODate(travelFormData?.claimFromDate),
    });
  }
  if (claimType?.Food) {
    claimDetails.push({
      ...foodFormData,
      claimFromDate: convertDateToISODate(foodFormData?.claimFromDate),
    });
  }
  if (claimType?.Accommodation) {
    claimDetails.push({
      ...accommodation,
      claimFromDate: convertDateToISODate(accommodation?.claimFromDate),
      claimToDate: convertDateToISODate(accommodation?.claimToDate),
    });
  }
  if (claimType?.Miscellaneous) {
    claimDetails.push({
      ...miscellaneous,
      claimFromDate: convertDateToISODate(miscellaneous?.claimFromDate),
    });
  }

  const formData = new FormData();
  const allSelectedFiles = Object.values(imagesList).flat();
  allSelectedFiles.forEach((file: any) => {
    formData.append('files', file);
  });
  const data = {
    taskAssignmentId: selectedTask?.taskAssignmentId || null,
    claimDetails: claimDetails,
  };
  return { data, allSelectedFiles, formData };
};

/**
 * Handles the file change event, validating the file size and the number of images.
 */
export const handleFileChange = async (
  event: React.ChangeEvent<HTMLInputElement>,
  imagesList: any,
  activeClaimUpload: string | number,
  setImagesList: (value: any) => void,
  setActiveClaimUpload: any,
  fileInputRef: any
) => {
  const files = Array.from(event.target.files || []);
  if (files && files.length > 0) {
    const filesCount =
      (imagesList?.[activeClaimUpload as keyof any]?.length || 0) +
      files?.length;
    if (filesCount > 9) {
      toast.error('You can only upload up to 9 images.');
      return;
    }
    let isGreaterThanMaxFileSize = false;
    const updatedFiles = files.map((file: any) => {
      if (file?.size > 1024 * 1024) {
        toast.error(
          'Each file must not exceed 1MB. Please upload smaller files.'
        );
        isGreaterThanMaxFileSize = true;
        return;
      }
      const newFileName =
        moment().format('YYYYMMDDSSSSS') +
        `_CT${activeClaimUpload}` +
        file.name.slice(file.name.lastIndexOf('.'));
      return new File([file], newFileName, { type: file.type });
    });
    if (isGreaterThanMaxFileSize) {
      return;
    }
    setImagesList({
      ...imagesList,
      [activeClaimUpload]: [
        ...(imagesList?.[activeClaimUpload] || []),
        ...updatedFiles,
      ],
    });
    setActiveClaimUpload('');
    event.target.value = '';
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.type = 'file';
    }
  }
};

/**
 * Makes an API call to get the reimbursement details based on the selected task.
 */
export const reimbursementLocationDetails = (
  setShowLoader: (isLoading: boolean) => void,
  dispatch: ThunkDispatch<any, any, any>,
  setDisableTravelField: (value: any) => void,
  setTravelFormData: (value: any) => void,
  selectedTask: any
) => {
  setShowLoader(true);
  dispatch(fetchReimbursementLocationDetails({ taskId: selectedTask?.taskId }))
    ?.then((fulfilled: any) => {
      setShowLoader(false);
      if (isAPISuccess(fulfilled?.payload)) {
        const travelApiData: any = {
          totalDistance: fulfilled?.payload?.result?.totalDistance || '',
          locationA: fulfilled?.payload?.result?.startPointAddress || '',
          locationB: fulfilled?.payload?.result?.endPointAddress || '',
        };
        if (fulfilled?.payload?.result?.completionDate) {
          travelApiData.claimFromDate = moment(
            fulfilled?.payload?.result?.completionDate
          ).format('DD-MM-YYYY H:mm');
        }
        setDisableTravelField(travelApiData);
        setTravelFormData((prev: any) => ({
          ...prev,
          ...travelApiData,
        }));
      }
    })
    ?.catch(() => {
      setShowLoader(false);
    });
};

/**
 * Uploads the specified attachments to the Reimbursement Attachments API
 */
export const uploadAttachments = (
  formData: any,
  params: any,
  setShowLoader: (isLoading: boolean) => void,
  dispatch: ThunkDispatch<any, any, any>,
  toggleModal: (action: string) => void
) => {
  setShowLoader(true);
  dispatch(
    postApplyReimbursementAttachments({ data: formData, params: params })
  )
    ?.then((fulfilled: any) => {
      setShowLoader(false);
      if (isAPIFailed(fulfilled?.payload)) {
        toast.error(fulfilled?.payload.responseInformation.responseMessage);
      } else if (isAPISuccess(fulfilled?.payload)) {
        toast.success(fulfilled?.payload.responseInformation.responseMessage);
        toggleModal(CLICK_ACTIONS.NO_ACTION);
      }
    })
    ?.catch(() => {
      setShowLoader(false);
    });
};

// Helper function to handle conditionally adding filter values
const addFilterToData = (
  data: any,
  filterValue: any,
  key: string,
  transformFn?: (value: any) => any
) => {
  if (filterValue?.value) {
    data[key] = transformFn
      ? transformFn(filterValue.value)
      : filterValue.value;
  }
};
const addFilterToDataForString = (
  data: any,
  filterValue: string,
  key: string
) => {
  if (filterValue) {
    data[key] = filterValue;
  }
};

/**
 * Creates the payload for fetching the approved reimbursement list.
 */
export const handlePayloadForApproveList = (
  currentPage: number,
  pageSize: number,
  filter?: ApproveReimbursementFilterType
) => {
  const data: any = {
    pageNumber: currentPage,
    pagePerCount: pageSize,
  };

  if (filter) {
    addFilterToDataForString(data, filter.employeeName, 'employeeName');
    addFilterToDataForString(data, filter.employeeId, 'employeeUniqueId');
    addFilterToData(data, filter.zone, 'zone', (value) => Number(value));
    addFilterToData(data, filter.state, 'state', (value) => Number(value));
    addFilterToData(data, filter.district, 'district', (value) =>
      Number(value)
    );
    addFilterToData(data, filter.city, 'city', (value) => Number(value));
    addFilterToData(data, filter.role, 'role');
    addFilterToData(data, filter.status, 'status');
  }
  return data;
};

/**
 * Opens the modal when the status is clicked.
 */
export const onTableAction = (
  param: any,
  columnHeader: ColumnHeaderType,
  setSelectedTask: any,
  setIsModalOpen: any
) => {
  setSelectedTask(param);
  setIsModalOpen(true);
};

/**
 * Opens the modal to display the details of the selected reimbursement.
 */
export const handleToggleModal = (
  action: any,
  setIsSuccessfullyApproveOpen: any,
  setIsModalOpen: any,
  getApproveReimbursementDataList: any,
  setSelectedTask: any,
  selectedFilter: any
) => {
  if (action === CLICK_ACTIONS?.REIMBURSEMENT_SUCCESS_MODAL) {
    setIsSuccessfullyApproveOpen(true);
    setIsModalOpen(false);
  } else {
    getApproveReimbursementDataList(selectedFilter);
    setIsModalOpen(false);
    setSelectedTask(null);
  }
};

/**
 * Displays a success modal after the reimbursement has been approved.
 */
export const handleApproveSuccessToggle = (
  setIsSuccessfullyApproveOpen: (value: any) => void,
  toggleModal: any,
  isSuccessfullyApproveOpen: boolean
) => {
  setIsSuccessfullyApproveOpen(!isSuccessfullyApproveOpen);
  toggleModal();
};

/**
 * Handles media uploads by validating the file sizes and the number of images based on custom props.
 */
export const mediaUploadHandleFiles = (
  fileArray: File[],
  supportedFiles: string[],
  selectedFiles: File[],
  maxFileSizeInBytes: number | undefined,
  maxFiles: number,
  setSelectedFiles: (arg: any[]) => void
) => {
  const supportedFileArray = fileArray.filter((file) => {
    return supportedFiles.includes(
      `.${file.name.split('.')?.pop()?.toLowerCase()}`
    );
  });
  const unsupportedFileArray = fileArray.filter(
    (file) =>
      !supportedFiles.includes(`.${file.name.split('.')?.pop()?.toLowerCase()}`)
  );

  if (unsupportedFileArray.length > 0) {
    toast.error(`Please upload only ${supportedFiles.join(', ')} ${' '}files.`);
    setSelectedFiles([]);
    return;
  }

  if (selectedFiles.length + supportedFileArray.length > maxFiles) {
    toast.error(
      `You can only upload up to ${maxFiles} ${maxFiles === 1 ? 'file' : 'files'}.`
    );
    return;
  }
  if (maxFileSizeInBytes) {
    let exceededFileSize = false;
    supportedFileArray.forEach((file: File) => {
      if (file.size > maxFileSizeInBytes) {
        toast.error(
          `Please ensure each file is smaller than ${maxFileSizeInBytes / (1024 * 1024)}MB `
        );
        exceededFileSize = true;
      }
    });
    if (exceededFileSize) return;
  }
  setSelectedFiles([...selectedFiles, ...supportedFileArray]);
};

/**
 * Creates a new file with specified size, name, and type.
 */
export const createFileWithSize = (
  sizeInBytes: number,
  name: string,
  type: string
) => {
  const content = 'a'.repeat(sizeInBytes);
  return new File([content], name, { type });
};
export const approveReimbursement = (
  status: number,
  comment: string,
  reimbursementDetails: any,
  setShowLoader: (isLoading: boolean) => void,
  dispatch: ThunkDispatch<any, any, any>,
  toggleModal: (action?: string) => void
) => {
  const data: any = {
    claimId: Number(reimbursementDetails?.claimId),
    status: status,
  };
  if (status === 1 && !comment.trim()) {
    toast.error('Comment is required');
    return;
  }
  if (comment) {
    data.comment = comment;
  }
  setShowLoader(true);
  dispatch(approveOrRejectReimbursement(data))
    ?.then((fulfilled: any) => {
      setShowLoader(false);
      if (isAPIFailed(fulfilled.payload)) {
        toast.error(fulfilled.payload.responseInformation.responseMessage);
      } else if (isAPISuccess(fulfilled.payload)) {
        if (status == 0) {
          toggleModal(CLICK_ACTIONS?.REIMBURSEMENT_SUCCESS_MODAL);
        } else {
          toast.success(fulfilled.payload.responseInformation.responseMessage);
          toggleModal();
        }
      }
    })
    .catch(() => {
      setShowLoader(false);
    });
};
